import React, { createContext, useContext, useMemo } from "react";

const ApiCtx = createContext(null);

/**
 * Webpack DefinePlugin replaces `process.env.REACT_APP_*` with the literal
 * string at build time, so the `process` global never has to actually exist
 * at runtime. (Earlier code used a `typeof process` guard, but in CRA 5 /
 * webpack 5 the process global isn't polyfilled in the browser, so that
 * guard was always false and BASE fell through to localhost:5000.)
 */
const RUNTIME_ENV =
  (typeof window !== "undefined" ? window.__ENV__ : undefined) || {};
const RUNTIME_API_BASE =
  (typeof window !== "undefined" ? window.__API_BASE__ : undefined) || undefined;

const BASE =
  process.env.REACT_APP_API_URL ||
  RUNTIME_ENV.REACT_APP_API_URL ||
  RUNTIME_API_BASE ||
  "http://127.0.0.1:8001";

const PUBLIC_BASE =
  process.env.REACT_APP_PUBLIC_API_URL ||
  RUNTIME_ENV.REACT_APP_PUBLIC_API_URL ||
  BASE;

/** ✅ Helper to safely join URLs */
const joinUrl = (base, path) => {
  const b = base.endsWith("/") ? base.slice(0, -1) : base;
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${b}${p}`;
};

/** ✅ Core request helper */
async function request(
  path,
  { method = "GET", body, headers = {}, timeoutMs = 15000 } = {}
) {
  return requestWithBase(BASE, path, { method, body, headers, timeoutMs });
}

async function requestWithBase(
  base,
  path,
  { method = "GET", body, headers = {}, timeoutMs = 15000 } = {}
) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const res = await fetch(joinUrl(base, path), {
      method,
      headers: { "Content-Type": "application/json", ...headers },
      body: body ? JSON.stringify(body) : undefined,
      signal: controller.signal,
      credentials: "include",
    });

    const text = await res.text().catch(() => "");
    const data = text
      ? (() => {
          try {
            return JSON.parse(text);
          } catch {
            return text;
          }
        })()
      : {};

    if (!res.ok) {
      const msg =
        (data && data.message) ||
        text ||
        res.statusText ||
        `HTTP ${res.status}`;
      const err = new Error(msg);
      try { err.status = res.status; } catch {}
      try { err.body = data; } catch {}
      throw err;
    }

    return data;
  } finally {
    clearTimeout(timeout);
  }
}

/** ✅ API Provider Component */
export function ApiProvider({ children }) {
  const value = useMemo(() => {
    // Whitelist fields to avoid circular JSON mistakes from UI state
    const sanitizePropertyPayload = (p = {}) => {
      const coerceArr = (v) => (Array.isArray(v) ? v : []);
      const coerceStrArr = (v) => coerceArr(v).map((x) => String(x));
      return {
        name: String(p.name || ""),
        slug: p.slug != null ? String(p.slug) : undefined,
        category: String(p.category || ""),
        status: String(p.status || ""),
        location: String(p.location || ""),
        address: String(p.address || ""),
        description: String(p.description || ""),
        price: Number(p.price || 0),
        agentName: p.agentName != null ? String(p.agentName) : undefined,
        agentEmail: p.agentEmail != null ? String(p.agentEmail) : undefined,
        agentPhone: p.agentPhone != null ? String(p.agentPhone) : undefined,
        agentWhatsapp: p.agentWhatsapp != null ? String(p.agentWhatsapp) : undefined,
        heroImage: p.heroImage != null ? String(p.heroImage) : undefined,
        gallery: coerceStrArr(p.gallery),
        amenities: coerceStrArr(p.amenities),
      };
    };
    // ---- Public (no auth) ----
    const fetchProperties = ({ location } = {}) => {
      const qs = new URLSearchParams();
      if (location) qs.set("location", location);
      const suffix = qs.toString() ? `?${qs.toString()}` : "";
      return requestWithBase(PUBLIC_BASE, `/api/properties${suffix}`);
    };

    const fetchProperty = (slug) =>
      requestWithBase(PUBLIC_BASE, `/api/properties/${encodeURIComponent(slug)}`);

    const fetchUnits = (slug) =>
      requestWithBase(PUBLIC_BASE, `/api/units?slug=${encodeURIComponent(slug)}`);

    const submitApplicationPublic = (payload) =>
      requestWithBase(PUBLIC_BASE, `/api/applications`, { method: 'POST', body: payload });

    // ---- Authenticated (admin) ----
    const getAuthHeaders = () => {
      const token = localStorage.getItem("token");
      // Support both JWT and admin token header used by server
      const adminToken =
        localStorage.getItem("adminToken") ||
        process.env.REACT_APP_ADMIN_TOKEN ||
        RUNTIME_ENV.REACT_APP_ADMIN_TOKEN;

      const headers = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;
      if (adminToken) headers["x-admin-token"] = adminToken;
      return headers;
    };

    // Admin list / show — sees drafts, paused, and active properties (unlike public)
    const adminListProperties = ({ location } = {}) => {
      const qs = new URLSearchParams();
      if (location) qs.set("location", location);
      const suffix = qs.toString() ? `?${qs.toString()}` : "";
      return request(`/api/admin/properties${suffix}`, { headers: getAuthHeaders() });
    };

    const adminGetProperty = (slug) =>
      request(`/api/admin/properties/${encodeURIComponent(slug)}`, { headers: getAuthHeaders() });

    const adminCreateProperty = (payload) =>
      request(`/api/admin/properties`, {
        method: "POST",
        body: sanitizePropertyPayload(payload),
        headers: getAuthHeaders(),
      });

    const adminUpdateProperty = (slug, payload) =>
      request(`/api/admin/properties/${encodeURIComponent(slug)}`, {
        method: "PUT",
        body: sanitizePropertyPayload(payload),
        headers: getAuthHeaders(),
      });

    const adminDeleteProperty = (slug) =>
      request(`/api/admin/properties/${encodeURIComponent(slug)}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });

    // Admin units — sees all unit statuses (vacant/reserved/occupied/maintenance/offline)
    const adminListUnits = (slug) => {
      const qs = slug ? `?slug=${encodeURIComponent(slug)}` : "";
      return request(`/api/admin/units${qs}`, { headers: getAuthHeaders() });
    };

    const adminListUnitsForMany = async (slugs = []) => {
      const pairs = await Promise.all(
        (slugs || []).map(async (s) => {
          try {
            const u = await adminListUnits(s);
            return [s, Array.isArray(u) ? u : []];
          } catch {
            return [s, []];
          }
        })
      );
      return Object.fromEntries(pairs);
    };

    const adminCreateUnit = (slug, payload) =>
      request(`/api/admin/units`, {
        method: "POST",
        body: { slug, ...(payload || {}) },
        headers: getAuthHeaders(),
      });

    const adminUpdateUnit = (id, payload) =>
      request(`/api/admin/units/${encodeURIComponent(id)}`, {
        method: "PUT",
        body: payload || {},
        headers: getAuthHeaders(),
      });

    const adminDeleteUnit = (id) =>
      request(`/api/admin/units/${encodeURIComponent(id)}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });

    const adminReleaseReservation = (unitId) =>
      request(`/api/admin/units/release-reservation`, {
        method: "PATCH",
        body: { unitId },
        headers: getAuthHeaders(),
      });

    // Public list helper retained for the public site; admin pages should use adminListUnitsForMany
    const fetchUnitsForMany = async (slugs = []) => {
      const pairs = await Promise.all(
        (slugs || []).map(async (s) => {
          try {
            const u = await requestWithBase(PUBLIC_BASE, `/api/units?slug=${encodeURIComponent(s)}`);
            return [s, Array.isArray(u) ? u : []];
          } catch {
            return [s, []];
          }
        })
      );
      return Object.fromEntries(pairs);
    };

    const adminGetMe = () =>
      request(`/api/auth/me`, { headers: getAuthHeaders() });

    const adminUpload = async (file, { folder } = {}) => {
      const fd = new FormData();
      fd.append("file", file);
      if (folder) fd.append("folder", folder);
      const controller = new AbortController();
      const res = await fetch(joinUrl(BASE, "/api/uploads"), {
        method: "POST",
        headers: { ...getAuthHeaders() },
        body: fd,
        credentials: "include",
        signal: controller.signal,
      });
      if (!res.ok) {
        const msg = await res.text().catch(() => res.statusText);
        throw new Error(msg || `Upload failed (${res.status})`);
      }
      return res.json();
    };

    // Public/general upload helper (uses admin token if present, but works without in dev)
    const uploadFile = async (file, { folder } = {}) => {
      const fd = new FormData();
      fd.append("file", file);
      if (folder) fd.append("folder", folder);
      const controller = new AbortController();
      const res = await fetch(joinUrl(BASE, "/api/uploads"), {
        method: "POST",
        // Try with admin token if available; server allows unauth in dev
        headers: { ...getAuthHeaders() },
        body: fd,
        credentials: "include",
        signal: controller.signal,
      });
      if (!res.ok) {
        const msg = await res.text().catch(() => res.statusText);
        throw new Error(msg || `Upload failed (${res.status})`);
      }
      return res.json();
    };

    const uploadPublicFile = async (file, { folder } = {}) => {
      const fd = new FormData();
      fd.append("file", file);
      if (folder) fd.append("folder", folder);
      const controller = new AbortController();
      const res = await fetch(joinUrl(PUBLIC_BASE, "/api/uploads"), {
        method: "POST",
        body: fd,
        credentials: "include",
        signal: controller.signal,
      });
      if (!res.ok) {
        const msg = await res.text().catch(() => res.statusText);
        throw new Error(msg || `Upload failed (${res.status})`);
      }
      return res.json();
    };

    // ---- Room Types (admin) ----
    const adminListRoomTypes = () =>
      request(`/api/admin/room-types`, { headers: getAuthHeaders() });

    const adminGetRoomType = (id) =>
      request(`/api/admin/room-types/${encodeURIComponent(id)}`, { headers: getAuthHeaders() });

    const adminCreateRoomType = (payload) =>
      request(`/api/admin/room-types`, { method: "POST", body: payload, headers: getAuthHeaders() });

    const adminUpdateRoomType = (id, payload) =>
      request(`/api/admin/room-types/${encodeURIComponent(id)}`, { method: "PUT", body: payload, headers: getAuthHeaders() });

    const adminDeleteRoomType = (id) =>
      request(`/api/admin/room-types/${encodeURIComponent(id)}`, { method: "DELETE", headers: getAuthHeaders() });

    const adminBulkImportRoomTypes = (list = []) =>
      request(`/api/admin/room-types/bulk`, { method: "POST", body: list, headers: getAuthHeaders() });

    // ---- Applicants / Tenants (admin) ----
    const adminListApplicants = ({ limit = 25, cursor, q, property, status } = {}) => {
      const qs = new URLSearchParams();
      if (limit) qs.set('limit', String(limit));
      if (cursor) qs.set('cursor', cursor);
      if (q) qs.set('q', q);
      if (property) qs.set('property', property);
      if (status) qs.set('status', status);
      return request(`/api/admin/applications?${qs.toString()}`, { headers: getAuthHeaders() });
    };

    const adminCreateApplicant = ({ propertySlug, fullName, email, phone, moveInDate, message }) =>
      request(`/api/admin/applications`, {
        method: 'POST',
        body: { propertySlug, fullName, email, phone, moveInDate, message },
        headers: getAuthHeaders(),
      });

    const adminApproveApplicant = ({ appId, unitId, monthlyPrice, rentAmount, depositAmount, startsOn, endsOn, notes }) =>
      request(`/api/admin/applications/approve`, {
        method: 'PATCH',
        body: { appId, unitId, monthlyPrice, rentAmount, depositAmount, startsOn, endsOn, notes },
        headers: getAuthHeaders(),
      });

    const adminRejectApplicant = (appId) =>
      request(`/api/admin/applications/reject`, { method: 'DELETE', body: { appId }, headers: getAuthHeaders() });

    const adminGetApplicant = (id) =>
      request(`/api/admin/applications/${encodeURIComponent(id)}`, { headers: getAuthHeaders() });

    const adminListTenants = ({ status, limit = 25, cursor, q, property, unit } = {}) => {
      const qs = new URLSearchParams();
      if (status) qs.set('status', status);
      if (limit) qs.set('limit', String(limit));
      if (cursor) qs.set('cursor', cursor);
      if (q) qs.set('q', q);
      if (property) qs.set('property', property);
      if (unit) qs.set('unit', unit);
      return request(`/api/admin/tenants?${qs.toString()}`, { headers: getAuthHeaders() });
    };

    const adminSetTenantStatus = ({ tenantId, status, reason }) =>
      request(`/api/admin/tenants/status`, { method: 'PATCH', body: { tenantId, status, reason }, headers: getAuthHeaders() });

    const adminAddTenantDocument = ({ tenantId, document }) =>
      request(`/api/admin/tenants/documents`, { method: 'POST', body: { tenantId, document }, headers: getAuthHeaders() });

    const adminGetTenant = (id) =>
      request(`/api/admin/tenants/${encodeURIComponent(id)}`, { headers: getAuthHeaders() });

    const adminMarkTenantLeaseSigned = ({ tenantId }) =>
      request(`/api/admin/tenants/${encodeURIComponent(tenantId)}/mark-lease-signed`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
      });

    const adminCompleteTenantMoveIn = ({ tenantId, moveInDate }) =>
      request(`/api/admin/tenants/${encodeURIComponent(tenantId)}/complete-move-in`, {
        method: 'PATCH',
        body: { moveInDate },
        headers: getAuthHeaders(),
      });

    const adminRequestTenantPayment = ({ tenantId, subject, body, cc, bcc, fromName, fromAddress }) =>
      request(`/api/admin/tenants/${encodeURIComponent(tenantId)}/request-payment`, {
        method: 'POST',
        body: { subject, body, cc, bcc, fromName, fromAddress },
        headers: getAuthHeaders(),
      });

    const adminGetBillingOverview = ({ month, search, status } = {}) => {
      const qs = new URLSearchParams();
      if (month) qs.set('month', month);
      if (search) qs.set('search', search);
      if (status) qs.set('status', status);
      return request(`/api/admin/billing/overview?${qs.toString()}`, { headers: getAuthHeaders() });
    };

    const adminGenerateBillingInvoices = ({ month } = {}) =>
      request(`/api/admin/billing/generate`, {
        method: 'POST',
        body: { month },
        headers: getAuthHeaders(),
      });

    const adminSendBillingReminder = ({ invoiceId }) =>
      request(`/api/admin/billing/invoices/${encodeURIComponent(invoiceId)}/send-reminder`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });

    const adminSendBillingBulkReminders = ({ invoiceIds }) =>
      request(`/api/admin/billing/reminders`, {
        method: 'POST',
        body: { invoiceIds },
        headers: getAuthHeaders(),
      });

    const adminRecordBillingPayment = async ({
      invoiceId,
      submittedByName,
      submittedByEmail,
      paymentReference,
      amountClaimed,
      approvedAmount,
      notes,
      reviewNotes,
      markAsPaid,
      files = [],
    }) => {
      const fd = new FormData();
      if (submittedByName != null) fd.append('submittedByName', submittedByName);
      if (submittedByEmail != null) fd.append('submittedByEmail', submittedByEmail);
      if (paymentReference != null) fd.append('paymentReference', paymentReference);
      if (amountClaimed != null && amountClaimed !== '') fd.append('amountClaimed', String(amountClaimed));
      if (approvedAmount != null && approvedAmount !== '') fd.append('approvedAmount', String(approvedAmount));
      if (notes != null) fd.append('notes', notes);
      if (reviewNotes != null) fd.append('reviewNotes', reviewNotes);
      fd.append('markAsPaid', markAsPaid ? '1' : '0');
      (files || []).forEach((file) => fd.append('files[]', file));

      const res = await fetch(joinUrl(BASE, `/api/admin/billing/invoices/${encodeURIComponent(invoiceId)}/record-payment`), {
        method: 'POST',
        headers: { ...getAuthHeaders() },
        body: fd,
        credentials: 'include',
      });

      const text = await res.text().catch(() => '');
      const data = text
        ? (() => {
            try {
              return JSON.parse(text);
            } catch {
              return text;
            }
          })()
        : {};

      if (!res.ok) {
        const msg = (data && data.message) || text || res.statusText || `HTTP ${res.status}`;
        const err = new Error(msg);
        try { err.status = res.status; } catch {}
        try { err.body = data; } catch {}
        throw err;
      }

      return data;
    };

    // Applications: public docs attach (no auth required by server)
    const applicationUploadDocuments = ({ appId, documents }) =>
      requestWithBase(PUBLIC_BASE, `/api/applications/upload`, { method: 'POST', body: { appId, documents } });

    const adminAddApplicantDocument = ({ appId, document }) =>
      request(`/api/admin/applications/documents`, { method: 'POST', body: { appId, document }, headers: getAuthHeaders() });

    const adminAssignUnitToApplicant = ({ appId, unitId }) =>
      request(`/api/admin/applications/assign-unit`, { method: 'PATCH', body: { appId, unitId }, headers: getAuthHeaders() });
    const adminUnassignUnitFromApplicant = ({ appId }) =>
      request(`/api/admin/applications/unassign-unit`, { method: 'PATCH', body: { appId }, headers: getAuthHeaders() });

    const adminGetUnit = (id) =>
      request(`/api/admin/units/${encodeURIComponent(id)}`, { headers: getAuthHeaders() });

    const adminSendLease = ({ appId, subject, body, attachmentUrl, attachmentName, cc, bcc, fromName, fromAddress }) =>
      request(`/api/admin/applications/${encodeURIComponent(appId)}/send-lease`, {
        method: 'POST',
        body: { subject, body, attachmentUrl, attachmentName, cc, bcc, fromName, fromAddress },
        headers: getAuthHeaders(),
      });

    const adminGetWebsiteSettings = () =>
      request(`/api/admin/website-settings`, { headers: getAuthHeaders() });

    const adminUpdateWebsiteSettings = (payload) =>
      request(`/api/admin/website-settings`, { method: 'PUT', body: payload, headers: getAuthHeaders() });

    const apiHealth = () => request(`/api/health`);

    // ---- Users (admin) ----
    const adminListUsers = () =>
      request(`/api/admin/users`, { headers: getAuthHeaders() });

    const adminCreateUser = ({ name, email, password, role = 'agent', phone, avatarUrl, isActive = true }) =>
      request(`/api/admin/users`, { method: 'POST', body: { name, email, password, role, phone, avatarUrl, isActive }, headers: getAuthHeaders() });

    const adminUpdateUser = (id, updates) =>
      request(`/api/admin/users/${encodeURIComponent(id)}`, { method: 'PATCH', body: updates, headers: getAuthHeaders() });

    const adminDeleteUser = (id) =>
      request(`/api/admin/users/${encodeURIComponent(id)}`, { method: 'DELETE', headers: getAuthHeaders() });

    const logout = () => {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("adminToken");
      window.location.href = "/login";
    };

    return {
      baseUrl: BASE,
      publicBaseUrl: PUBLIC_BASE,
      // Public
      fetchProperties,
      fetchProperty,
      fetchUnits,
      submitApplicationPublic,
      uploadPublicFile,
      // Admin
      adminListProperties,
      adminGetProperty,
      adminCreateProperty,
      adminUpdateProperty,
      adminDeleteProperty,
      adminListUnits,
      adminListUnitsForMany,
      adminCreateUnit,
      adminUpdateUnit,
      adminDeleteUnit,
      adminReleaseReservation,
      fetchUnitsForMany,
      adminGetMe,
      adminUpload,
      adminListRoomTypes,
      adminGetRoomType,
      adminCreateRoomType,
      adminUpdateRoomType,
      adminDeleteRoomType,
      adminBulkImportRoomTypes,
      uploadFile,
      // Users
      adminListUsers,
      adminCreateUser,
      adminUpdateUser,
      adminDeleteUser,
      adminListApplicants,
      adminCreateApplicant,
      adminApproveApplicant,
      adminRejectApplicant,
      adminGetApplicant,
      adminAddApplicantDocument,
      adminAssignUnitToApplicant,
      adminUnassignUnitFromApplicant,
      adminGetUnit,
      adminSendLease,
      adminGetWebsiteSettings,
      adminUpdateWebsiteSettings,
      adminListTenants,
      adminSetTenantStatus,
      adminAddTenantDocument,
      adminGetTenant,
      adminMarkTenantLeaseSigned,
      adminCompleteTenantMoveIn,
      adminRequestTenantPayment,
      adminGetBillingOverview,
      adminGenerateBillingInvoices,
      adminSendBillingReminder,
      adminSendBillingBulkReminders,
      adminRecordBillingPayment,
      applicationUploadDocuments,
      apiHealth,
      logout,
    };
  }, []);

  return <ApiCtx.Provider value={value}>{children}</ApiCtx.Provider>;
}

/** ✅ Hook for use inside components */
export function useApi() {
  const ctx = useContext(ApiCtx);
  if (!ctx) throw new Error("useApi must be used within <ApiProvider>");
  return ctx;
}
